import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { ROUTES } from "@/constants/routes.constants";
import { resetPassword } from "@/apis/medplum";

import {
  Button,
  Card,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@/components/base";

const formSchema = z.object({
  email: z.string().email(),
});

type FormValues = z.infer<typeof formSchema>;

type LocationState = {
  email?: string;
};

export const ResetPasswordPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: locationState?.email ?? "" },
  });

  const handleSubmit = async ({ email }: FormValues) => {
    try {
      await resetPassword({ email, locale: i18n.language });
      toast.success(
        t("If this email is registered, you'll receive a reset link shortly.")
      );
      navigate(ROUTES.LOGIN);
    } catch {
      toast.error(t("Something went wrong. Please try again."));
    }
  };

  return (
    <Card
      padding="lg"
      className="flex w-full max-w-md -translate-y-1/6 flex-col gap-6"
    >
      <div className="flex flex-col items-center gap-2">
        <h1 className="text-3xl font-semibold">{t("Reset password")}</h1>
        <p className="text-muted-foreground text-center text-sm">
          {t(
            "Enter your email and we'll send you a link to reset your password."
          )}
        </p>
      </div>
      <Form {...form}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit(handleSubmit)(event);
          }}
          className="flex flex-col gap-4"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("Email")}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t("you@example.com")}
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            variant="primary"
            className="w-full"
            loading={form.formState.isSubmitting}
          >
            {t("Reset")}
          </Button>
        </form>
      </Form>
      <Button
        className="-mt-4"
        variant="tertiary"
        onClick={() => navigate(ROUTES.LOGIN)}
      >
        {t("Back to sign in")}
      </Button>
    </Card>
  );
};
